import { Bell, Copy, KeyRound, LogOut, Moon, Plug, RefreshCw, Smartphone, Sparkles, Trash2, Webhook } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { CLAUDE_MODELS, WEBHOOK_EVENTS, type AllSettings, type ApiKeyDTO, type TravelMode, type WebhookDTO } from '@tempo/core';
import { api } from '../api';
import { Button, Field, Loading, Modal, NumberInput, Segmented, Switch } from '../components/ui';
import { MODE_LABEL, relative } from '../lib/format';
import { locationSharing, useAction, useClaudeStatus, useFeatures, usePlaces, useSettings } from '../lib/hooks';
import { disablePush, enablePush, pushStatus } from '../lib/push';
import { setTheme, useTheme, type ThemePref } from '../lib/theme';
import { toast } from '../lib/toast';

type Section<K extends keyof AllSettings> = AllSettings[K];

function useSave<K extends keyof AllSettings>(section: K) {
  return useAction((patch: Partial<Section<K>>) => api.patch<AllSettings>(`/settings/${section}`, patch), 'Saved');
}

function Card({ icon, title, children, intro }: { icon?: ReactNode; title: string; intro?: ReactNode; children: ReactNode }) {
  return (
    <div className="card">
      <div className="card-head">
        {icon}
        <h2 className="grow">{title}</h2>
      </div>
      {intro && (
        <p className="small subtle" style={{ marginBottom: 14 }}>
          {intro}
        </p>
      )}
      <div className="stack">{children}</div>
    </div>
  );
}

export function SettingsPage() {
  const settings = useSettings();
  if (settings.isLoading || !settings.data) return <Loading />;
  const s = settings.data;
  return (
    <div className="page">
      <div className="page-head desktop-only">
        <h1>Settings</h1>
      </div>
      <div className="stack" style={{ gap: 16 }}>
        <AppearanceCard />
        <DaySettings s={s} />
        <PlanningSettings s={s} />
        <TravelSettings s={s} />
        <NotificationSettings s={s} />
        {s.assistant && <AssistantSettingsCard s={s} /> /* older servers (before a restart) don't have it */}
        <FeedCard />
        <IntegrationsCard s={s} />
        <AccountCard />
      </div>
    </div>
  );
}

/** Light or dark, on this device. */
function AppearanceCard() {
  const theme = useTheme();
  return (
    <Card icon={<Moon size={18} />} title="Appearance" intro="Applies to this device. “Match device” follows your phone or computer's light/dark setting.">
      <Segmented<ThemePref>
        value={theme}
        onChange={setTheme}
        options={[
          { value: 'system', label: 'Match device' },
          { value: 'light', label: 'Light' },
          { value: 'dark', label: 'Dark' },
        ]}
      />
    </Card>
  );
}

function DaySettings({ s }: { s: AllSettings }) {
  const save = useSave('planner');
  const [p, setP] = useState(s.planner);
  useEffect(() => setP(s.planner), [s.planner]);
  const dirty = JSON.stringify(p) !== JSON.stringify(s.planner);
  const zones = ['Europe/London', 'Europe/Dublin', 'Europe/Paris', 'UTC'];
  return (
    <Card title="Your day" intro="Nothing flexible is planned outside these hours. After a late finish (e.g. a shift until 1am) your next morning starts later to protect your sleep.">
      <div className="grid-3">
        <Field label="Day starts">
          <input className="input" type="time" value={p.dayStart} onChange={(e) => setP({ ...p, dayStart: e.target.value })} />
        </Field>
        <Field label="Bedtime">
          <input className="input" type="time" value={p.dayEnd} onChange={(e) => setP({ ...p, dayEnd: e.target.value })} />
        </Field>
        <Field label="Protect sleep (hours)">
          <NumberInput value={p.minSleepMin / 60} onChange={(v) => setP({ ...p, minSleepMin: Math.round((v ?? 0) * 60) })} min={0} max={14} step={0.5} />
        </Field>
      </div>
      <Field label="Timezone">
        <select className="input" value={p.timezone} onChange={(e) => setP({ ...p, timezone: e.target.value })}>
          {[...new Set([p.timezone, ...zones])].map((z) => (
            <option key={z}>{z}</option>
          ))}
        </select>
      </Field>
      {dirty && (
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={() => setP(s.planner)}>Undo</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(p)}>
            Save & re-plan
          </Button>
        </div>
      )}
    </Card>
  );
}

function PlanningSettings({ s }: { s: AllSettings }) {
  const save = useSave('planner');
  const saveTasks = useSave('tasks');
  const [p, setP] = useState(s.planner);
  useEffect(() => setP(s.planner), [s.planner]);
  const dirty = JSON.stringify(p) !== JSON.stringify(s.planner);
  const focus = p.focusWindows[0];
  return (
    <Card title="Planning" intro="How Tempo schedules study and task sessions.">
      <div className="grid-3">
        <Field label="Max study per day (h)">
          <NumberInput value={p.maxTaskMinPerDay / 60} onChange={(v) => setP({ ...p, maxTaskMinPerDay: Math.round((v ?? 0) * 60) })} min={0} max={16} step={0.5} />
        </Field>
        <Field label="Break after a session (min)">
          <NumberInput value={p.breakMin} onChange={(v) => setP({ ...p, breakMin: v ?? 0 })} min={0} max={120} step={5} />
        </Field>
        <Field label="Finish before deadlines by (h)">
          <NumberInput value={p.taskDeadlineBufferMin / 60} onChange={(v) => setP({ ...p, taskDeadlineBufferMin: Math.round((v ?? 0) * 60) })} min={0} step={1} />
        </Field>
      </div>
      <div className="grid-3">
        <Field label="Plan ahead (days)">
          <NumberInput value={p.horizonDays} onChange={(v) => setP({ ...p, horizonDays: v ?? 14 })} min={1} max={60} />
        </Field>
        <Field label="Start times on the" hint="minute grid">
          <select className="input" value={p.granularityMin} onChange={(e) => setP({ ...p, granularityMin: Number(e.target.value) })}>
            {[5, 10, 15, 30].map((g) => (
              <option key={g} value={g}>
                {g} min
              </option>
            ))}
          </select>
        </Field>
        <Field label="Pop home if I'd get (min)" hint="there, between commitments">
          <NumberInput value={p.returnHomeMinStayMin} onChange={(v) => setP({ ...p, returnHomeMinStayMin: v })} min={0} step={15} placeholder="never" />
        </Field>
      </div>
      <Field label="Classes finish early by (min)" hint="Southampton teaching slots start on the hour and run 45 min – the gap is used to walk between buildings.">
        <NumberInput value={p.classEndsEarlyMin} onChange={(v) => setP({ ...p, classEndsEarlyMin: v ?? 0 })} min={0} max={30} step={5} />
      </Field>
      <Switch
        on={!!focus}
        onChange={(on) => setP({ ...p, focusWindows: on ? [{ start: '09:00', end: '21:00' }] : [] })}
        label="Only schedule study during set hours"
        hint={focus ? undefined : 'Off: any time in your day'}
      />
      {focus && (
        <div className="row">
          <input className="input sm" type="time" value={focus.start} onChange={(e) => setP({ ...p, focusWindows: [{ ...focus, start: e.target.value }] })} />
          <span className="faint">to</span>
          <input className="input sm" type="time" value={focus.end} onChange={(e) => setP({ ...p, focusWindows: [{ ...focus, end: e.target.value }] })} />
        </div>
      )}
      <Switch
        on={s.tasks.autoCreditSessions}
        onChange={(v) => saveTasks.mutate({ autoCreditSessions: v })}
        label="Count study sessions as done once they pass"
        hint="Mark one as missed and it gets rescheduled"
      />
      {dirty && (
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={() => setP(s.planner)}>Undo</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(p)}>
            Save & re-plan
          </Button>
        </div>
      )}
    </Card>
  );
}

function TravelSettings({ s }: { s: AllSettings }) {
  const save = useSave('travel');
  const [google, setGoogle] = useState('');
  const [ors, setOrs] = useState('');
  const places = usePlaces().data ?? [];
  const unlocated = places.filter((p) => p.lat == null);
  return (
    <Card
      title="Travel"
      intro="Used for journey times, leave-by alerts and directions. Without an API key Tempo estimates from distance – add Google for real public transport and traffic times."
    >
      {unlocated.length > 0 && (
        <div className="callout warn">
          {unlocated.map((p) => p.name).join(', ')} {unlocated.length === 1 ? 'has' : 'have'} no location yet – set it under Places so travel can be worked out.
        </div>
      )}
      <Field label="Usually I get around by">
        <Segmented
          value={s.travel.defaultMode}
          onChange={(m: TravelMode) => save.mutate({ defaultMode: m })}
          options={(['walk', 'cycle', 'transit', 'drive'] as TravelMode[]).map((m) => ({ value: m, label: MODE_LABEL[m][0].toUpperCase() + MODE_LABEL[m].slice(1) }))}
        />
      </Field>
      <Field label="Journey times from">
        <select className="input" value={s.travel.provider} onChange={(e) => save.mutate({ provider: e.target.value as AllSettings['travel']['provider'] })}>
          <option value="auto">Best available (Google → OpenRouteService → estimate)</option>
          <option value="google">Google Maps only</option>
          <option value="ors">OpenRouteService only</option>
          <option value="estimate">Offline estimate only</option>
        </select>
      </Field>
      <div className="grid-2">
        <Field
          label="Google Maps API key"
          hint={
            <>
              Enable the <i>Routes API</i> + <i>Geocoding API</i> in Google Cloud. {s.travel.googleApiKey ? `Saved (${s.travel.googleApiKey})` : 'Not set'}
            </>
          }
        >
          <div className="row">
            <input className="input" type="password" autoComplete="off" value={google} onChange={(e) => setGoogle(e.target.value)} placeholder={s.travel.googleApiKey ?? 'AIza…'} />
            <Button disabled={!google} onClick={() => save.mutateAsync({ googleApiKey: google }).then(() => setGoogle(''))}>
              Save
            </Button>
          </div>
        </Field>
        <Field label="OpenRouteService key" hint={<>Free at openrouteservice.org. {s.travel.orsApiKey ? `Saved (${s.travel.orsApiKey})` : 'Not set'}</>}>
          <div className="row">
            <input className="input" type="password" autoComplete="off" value={ors} onChange={(e) => setOrs(e.target.value)} placeholder={s.travel.orsApiKey ?? 'key'} />
            <Button disabled={!ors} onClick={() => save.mutateAsync({ orsApiKey: ors }).then(() => setOrs(''))}>
              Save
            </Button>
          </div>
        </Field>
      </div>
      <GoogleKeyTest hasKey={!!s.travel.googleApiKey} />
      {(s.travel.googleApiKey || s.travel.orsApiKey) && (
        <div className="row">
          {s.travel.googleApiKey && !s.travel.googleApiKey.includes('(from .env)') && (
            <Button size="sm" variant="ghost" onClick={() => save.mutate({ googleApiKey: '' as never })}>
              Remove Google key
            </Button>
          )}
          {s.travel.orsApiKey && (
            <Button size="sm" variant="ghost" onClick={() => save.mutate({ orsApiKey: '' as never })}>
              Remove ORS key
            </Button>
          )}
        </div>
      )}
      <LocationSharing />
    </Card>
  );
}

function GoogleKeyTest({ hasKey }: { hasKey: boolean }) {
  const [result, setResult] = useState<{ ok: boolean; geocoding: string; routes: string } | null>(null);
  const test = useAction(async () => {
    const r = await api.post<{ ok: boolean; geocoding: string; routes: string }>('/travel/test');
    setResult(r);
    return r;
  });
  if (!hasKey) return null;
  return (
    <div className="stack tight">
      <Button size="sm" style={{ alignSelf: 'flex-start' }} onClick={() => test.mutate()} loading={test.isPending}>
        Test Google key
      </Button>
      {result && (
        <div className={`callout ${result.ok ? '' : 'danger'} small`}>
          <div className="stack tight">
            <div>
              <b>Places / addresses:</b> {result.geocoding}
            </div>
            <div>
              <b>Journeys:</b> {result.routes}
            </div>
            {!result.ok && (
              <div>
                In Google Cloud: enable <b>Routes API</b> and <b>Geocoding API</b> for the key's project, and make sure billing is set up
                (personal use stays inside Google's free monthly allowance).
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LocationSharing() {
  const [on, setOn] = useState(locationSharing.get());
  return (
    <Switch
      on={on}
      onChange={(v) => {
        locationSharing.set(v);
        setOn(v);
        if (v) toast('This device will share its location while Tempo is open');
      }}
      label="Share this device's location while Tempo is open"
      hint="Makes leave-by times live: if you're not where the plan expects, alerts are re-timed from where you are. (For background tracking use OwnTracks – see docs.)"
    />
  );
}

function AssistantSettingsCard({ s }: { s: AllSettings }) {
  const save = useSave('assistant');
  const qc = useQueryClient();
  const a = s.assistant;
  const cli = a.engine !== 'api';
  const { claudeCli } = useFeatures();
  const status = useClaudeStatus();
  const [key, setKey] = useState('');
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const test = useAction(async () => {
    const r = await api.post<{ ok: boolean; message: string }>('/assistant/test');
    setResult(r);
    void qc.invalidateQueries({ queryKey: ['claude-status'] });
    return r;
  });
  const setEngine = (engine: 'cli' | 'api') =>
    save.mutateAsync({ engine }).then(() => {
      setResult(null);
      void qc.invalidateQueries({ queryKey: ['claude-status'] });
    });
  const fromEnv = !!a.claudeApiKey?.includes('(from environment)');
  const st = status.data;
  const copy = (t: string) => navigator.clipboard.writeText(t).then(() => toast('Copied'));
  return (
    <Card
      icon={<Sparkles size={18} />}
      title="Assistant"
      intro="The Assistant mode works out what you're doing and offers a tool or two. In lectures it can transcribe live and – with Claude – explain what's being taught as it goes."
    >
      {claudeCli ? (
        <Field
          label="Claude runs through"
          hint={
            cli
              ? 'Claude Code on your PC, signed in with your Claude account – no API key, uses your plan’s limits. An online Tempo asks your PC through the relay.'
              : 'An Anthropic API key, pay as you go (console.anthropic.com).'
          }
        >
          <Segmented
            value={cli ? 'cli' : 'api'}
            options={[
              { value: 'cli', label: 'Claude on my PC' },
              { value: 'api', label: 'API key' },
            ]}
            onChange={(v) => void setEngine(v)}
          />
        </Field>
      ) : (
        <p className="small subtle">Claude runs on your own Anthropic API key, pay as you go: console.anthropic.com → API keys → Create key, then paste it below.</p>
      )}
      {cli && st && (
        <div className={`callout small ${st.ready ? '' : 'danger'}`}>
          {st.message}
          {st.problem === 'signin' && st.signInCommand && (
            <div className="stack" style={{ marginTop: 8, gap: 6 }}>
              <span>On your PC, open Command Prompt and run this once, then sign in in the browser:</span>
              <code className="mono" style={{ wordBreak: 'break-all' }}>{st.signInCommand}</code>
              <div>
                <Button size="sm" onClick={() => copy(st.signInCommand!)}>
                  <Copy size={14} /> Copy command
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {!cli && (
        <Field label="Claude API key" hint={a.claudeApiKey ? `Saved (${a.claudeApiKey})` : 'Not set – lectures still get the live transcript, glossary terms and marks.'}>
          <div className="row">
            <input className="input" type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder={a.claudeApiKey ?? 'sk-ant-…'} />
            <Button disabled={!key} onClick={() => save.mutateAsync({ claudeApiKey: key }).then(() => setKey(''))}>
              Save
            </Button>
          </div>
        </Field>
      )}
      <Field label="Model for live explanations" hint={(() => {
        const m = CLAUDE_MODELS.find((x) => x.id === a.model);
        return m ? (cli ? m.planNote : m.note) : 'Custom model';
      })()}>
        <select className="input" value={a.model} onChange={(e) => save.mutate({ model: e.target.value })}>
          {CLAUDE_MODELS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
          {!CLAUDE_MODELS.some((m) => m.id === a.model) && <option value={a.model}>{a.model}</option>}
        </select>
      </Field>
      <div className="grid-2">
        <Field label="Explain every (seconds)" hint="Only when there's new speech">
          <NumberInput value={a.explainIntervalSec} onChange={(v) => v && save.mutate({ explainIntervalSec: Math.max(30, v) })} min={30} max={600} step={15} />
        </Field>
        <Field label="Max explanations a day" hint={cli ? 'Keeps lectures from using up your plan' : 'A safety cap on cost'}>
          <NumberInput value={a.dailyExplainCap} onChange={(v) => v && save.mutate({ dailyExplainCap: v })} min={1} step={50} />
        </Field>
      </div>
      <Switch
        on={a.keepTranscripts}
        onChange={(v) => save.mutate({ keepTranscripts: v })}
        label="Keep lecture transcripts"
        hint="Off: transcripts only live on your screen during the lecture. On: saved in Tempo so you can review them later."
      />
      <div className="row wrap">
        {(cli || a.claudeApiKey) && (
          <Button size="sm" onClick={() => test.mutate()} loading={test.isPending}>
            {cli ? 'Check Claude' : 'Test Claude key'}
          </Button>
        )}
        {!cli && a.claudeApiKey && !fromEnv && (
          <Button size="sm" variant="ghost" onClick={() => save.mutate({ claudeApiKey: '' as never })}>
            Remove key
          </Button>
        )}
      </div>
      {result && <div className={`callout small ${result.ok ? '' : 'danger'}`}>{result.message}</div>}
    </Card>
  );
}

function NotificationSettings({ s }: { s: AllSettings }) {
  const save = useSave('notify');
  const [status, setStatus] = useState<string>('…');
  const [ntfy, setNtfy] = useState(s.notify.ntfyUrl ?? '');
  const refresh = () => pushStatus().then(setStatus);
  useEffect(() => {
    refresh();
  }, []);
  const test = useAction(() => api.post<{ push: number; ntfy: boolean | null }>('/notify/test'), (r) => `Sent to ${r.push} device${r.push === 1 ? '' : 's'}${r.ntfy ? ' + ntfy' : ''}`);
  const n = s.notify;
  return (
    <Card icon={<Bell size={18} />} title="Notifications" intro="“Leave in 10 min for your shift”, reminders and a morning summary.">
      <div className="row wrap">
        <Smartphone size={16} className="faint" />
        <span className="grow small">
          This device:{' '}
          <b>{status === 'on' ? 'push on' : status === 'off' ? 'push off' : status === 'denied' ? 'blocked in browser settings' : status === 'unsupported' ? 'not supported here' : '…'}</b>
        </span>
        {status === 'off' && (
          <Button size="sm" variant="primary" onClick={() => enablePush().then(refresh).then(() => toast('Push enabled')).catch((e) => toast(e.message, 'error'))}>
            Enable push
          </Button>
        )}
        {status === 'on' && (
          <Button size="sm" onClick={() => disablePush().then(refresh)}>
            Turn off
          </Button>
        )}
        {status === 'unsupported' && /iPhone|iPad/.test(navigator.userAgent) && <span className="tiny faint">Add Tempo to your Home Screen first</span>}
        <Button size="sm" onClick={() => test.mutate()} loading={test.isPending}>
          Send test
        </Button>
      </div>
      <Field label="ntfy topic URL (optional)" hint="Alternative to push: install the free ntfy app and subscribe to a hard-to-guess topic, e.g. https://ntfy.sh/tempo-x7k2q9">
        <div className="row">
          <input className="input" value={ntfy} onChange={(e) => setNtfy(e.target.value)} placeholder="https://ntfy.sh/your-secret-topic" />
          <Button disabled={ntfy === (n.ntfyUrl ?? '')} onClick={() => save.mutate({ ntfyUrl: ntfy.trim() || null })}>
            Save
          </Button>
        </div>
      </Field>
      <div className="grid-3">
        <Field label="Warn me before leaving (min)">
          <NumberInput value={n.leaveLeadMin} onChange={(v) => save.mutate({ leaveLeadMin: v ?? 0 })} min={0} max={120} step={5} />
        </Field>
        <Field label="Remind before events (min)" hint="Empty = off">
          <NumberInput value={n.eventReminderMin} onChange={(v) => save.mutate({ eventReminderMin: v })} min={0} max={240} step={5} placeholder="off" />
        </Field>
        <Field label="Morning summary at" hint="Empty = off">
          <input className="input" type="time" value={n.morningBriefTime ?? ''} onChange={(e) => save.mutate({ morningBriefTime: e.target.value || null })} />
        </Field>
      </div>
      <Switch on={n.startReminders} onChange={(v) => save.mutate({ startReminders: v })} label="Ping me when a routine or study session starts" />
    </Card>
  );
}

function FeedCard() {
  const feed = useQuery({ queryKey: ['feed'], queryFn: () => api.get<{ url: string; webcal: string }>('/feed') });
  const qc = useQueryClient();
  const regen = useAction(() => api.post('/feed/regenerate'), 'New link created – update your subscriptions');
  const copy = (t: string) => navigator.clipboard.writeText(t).then(() => toast('Copied'));
  return (
    <Card
      icon={<RefreshCw size={18} />}
      title="See it in your phone's calendar"
      intro="Subscribe to this private link in Google Calendar (Other calendars → From URL), Apple Calendar (Add Subscription) or Outlook. It includes everything Tempo plans, including leave-by times. Google refreshes subscriptions slowly (every few hours) – Tempo itself is always live."
    >
      {feed.data ? (
        <>
          <div className="secret">{feed.data.url}</div>
          <div className="row wrap">
            <Button size="sm" onClick={() => copy(feed.data!.url)}>
              <Copy size={14} /> Copy link
            </Button>
            <a className="btn sm" href={feed.data.webcal}>
              Open in calendar app
            </a>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => confirm('Make a new link? The old one stops working.') && regen.mutateAsync().then(() => qc.invalidateQueries({ queryKey: ['feed'] }))}
            >
              New link
            </Button>
          </div>
          <p className="tiny faint">
            Add <code>?travel=0</code>, <code>?routines=0</code>, <code>?tasks=0</code> or <code>?events=0</code> to the link to leave things out.
          </p>
        </>
      ) : (
        <Loading />
      )}
    </Card>
  );
}

function IntegrationsCard({ s }: { s: AllSettings }) {
  const keys = useQuery({ queryKey: ['apiKeys'], queryFn: () => api.get<ApiKeyDTO[]>('/api-keys') });
  const hooks = useQuery({ queryKey: ['webhooks'], queryFn: () => api.get<WebhookDTO[]>('/webhooks') });
  const [newKey, setNewKey] = useState<string | null>(null);
  const [keyName, setKeyName] = useState('');
  const [hookUrl, setHookUrl] = useState('');
  const [hookSecret, setHookSecret] = useState<string | null>(null);
  const createKey = useAction(() => api.post<{ secret: string }>('/api-keys', { name: keyName.trim() }));
  const revoke = useAction((id: string) => api.del(`/api-keys/${id}`), 'Key revoked');
  const addHook = useAction(() => api.post<{ secret: string }>('/webhooks', { url: hookUrl.trim(), events: [...WEBHOOK_EVENTS] }));
  const delHook = useAction((id: string) => api.del(`/webhooks/${id}`), 'Webhook removed');
  const testHook = useAction((id: string) => api.post<{ status: number | null; error: string | null }>(`/webhooks/${id}/test`), (r) => (r.error ? `Failed: ${r.error}` : `Delivered (HTTP ${r.status})`));
  const bridgeSave = useSave('bridge');
  const bridgeSync = useAction(() => api.post('/bridge/sync'), 'School Manager synced');
  const b = s.bridge;
  const { schoolManager } = useFeatures();

  return (
    <Card icon={<Plug size={18} />} title="Integrations" intro="Connect your uni-work bot and other tools. Full API reference: docs/API.md in the project.">
      {schoolManager && (
        <>
          <div className="section-title">School Manager (file bridge)</div>
          <Switch on={b.enabled} onChange={(v) => bridgeSave.mutate({ enabled: v })} label="Exchange files with School Manager" hint="Deadlines & study requests in, placements / timetable / free time out (sm-cal/1)." />
          <div className="small subtle">
            {b.lastReadAt ? `Last read ${relative(b.lastReadAt)}` : 'Not read yet'} · {b.lastWriteAt ? `last written ${relative(b.lastWriteAt)}` : 'not written yet'}
          </div>
          {b.lastError && <div className="callout danger small">{b.lastError}</div>}
          <details className="small">
            <summary className="faint">File locations</summary>
            <div className="stack tight" style={{ marginTop: 8 }}>
              <div>
                In: <code>{b.inPath}</code>
              </div>
              <div>
                Out: <code>{b.outPath}</code>
              </div>
            </div>
          </details>
          <Button size="sm" style={{ alignSelf: 'flex-start' }} onClick={() => bridgeSync.mutate()} loading={bridgeSync.isPending}>
            <RefreshCw size={14} /> Sync now
          </Button>
        </>
      )}

      <div className="section-title">
        <KeyRound size={12} /> API keys
      </div>
      {(keys.data ?? []).map((k) => (
        <div key={k.id} className="row small">
          <span className="grow">
            <b>{k.name}</b> <code className="faint">{k.prefix}…</code> · {k.lastUsedAt ? `used ${relative(k.lastUsedAt)}` : 'never used'}
          </span>
          <Button size="sm" variant="ghost" icon onClick={() => confirm(`Revoke "${k.name}"? Anything using it will stop working.`) && revoke.mutate(k.id)} aria-label="Revoke">
            <Trash2 size={14} />
          </Button>
        </div>
      ))}
      <div className="row">
        <input className="input" value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="Name, e.g. uni bot" />
        <Button disabled={!keyName.trim()} onClick={() => createKey.mutateAsync().then((r) => (setNewKey(r.secret), setKeyName(''))).catch(() => undefined)}>
          Create key
        </Button>
      </div>

      <div className="section-title">
        <Webhook size={12} /> Webhooks
      </div>
      {(hooks.data ?? []).map((h) => (
        <div key={h.id} className="row small">
          <span className="grow truncate">
            <code>{h.url}</code>
            <span className="faint"> · {h.lastDeliveredAt ? `${h.lastError ? `failed: ${h.lastError}` : `ok`} ${relative(h.lastDeliveredAt)}` : 'not used yet'}</span>
          </span>
          <Button size="sm" onClick={() => testHook.mutate(h.id)}>
            Test
          </Button>
          <Button size="sm" variant="ghost" icon onClick={() => delHook.mutate(h.id)} aria-label="Remove">
            <Trash2 size={14} />
          </Button>
        </div>
      ))}
      <div className="row">
        <input className="input" value={hookUrl} onChange={(e) => setHookUrl(e.target.value)} placeholder="https://my-bot.example/tempo-webhook" />
        <Button disabled={!/^https?:\/\//.test(hookUrl)} onClick={() => addHook.mutateAsync().then((r) => (setHookSecret(r.secret), setHookUrl(''))).catch(() => undefined)}>
          Add
        </Button>
      </div>

      {newKey && (
        <Modal title="Your new API key" onClose={() => setNewKey(null)} footer={<Button variant="primary" onClick={() => setNewKey(null)}>Done</Button>}>
          <div className="stack">
            <p className="small subtle">Copy it now – it won't be shown again. Send it as <code>Authorization: Bearer &lt;key&gt;</code>.</p>
            <div className="secret">{newKey}</div>
            <Button onClick={() => navigator.clipboard.writeText(newKey).then(() => toast('Copied'))}>
              <Copy size={14} /> Copy
            </Button>
          </div>
        </Modal>
      )}
      {hookSecret && (
        <Modal title="Webhook signing secret" onClose={() => setHookSecret(null)} footer={<Button variant="primary" onClick={() => setHookSecret(null)}>Done</Button>}>
          <div className="stack">
            <p className="small subtle">
              Each delivery has <code>X-Tempo-Signature: sha256=HMAC(secret, body)</code>. Verify it in your bot. Shown once.
            </p>
            <div className="secret">{hookSecret}</div>
          </div>
        </Modal>
      )}
    </Card>
  );
}

function AccountCard() {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const change = useAction(() => api.post('/auth/password', { current: cur, next, signOutOthers: true }), 'Password changed – other devices signed out');
  const logout = async () => {
    await api.post('/auth/logout');
    navigator.serviceWorker?.controller?.postMessage('clear-cache');
    location.reload();
  };
  return (
    <Card title="Account">
      <div className="grid-2">
        <Field label="Current password">
          <input className="input" type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} />
        </Field>
        <Field label="New password">
          <input className="input" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
      </div>
      <div className="row wrap">
        <Button disabled={!cur || next.length < 8} loading={change.isPending} onClick={() => change.mutateAsync().then(() => (setCur(''), setNext(''))).catch(() => undefined)}>
          Change password
        </Button>
        <span className="grow" />
        <Button variant="danger" onClick={logout}>
          <LogOut size={16} /> Sign out
        </Button>
      </div>
    </Card>
  );
}
